import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  Button,
  Divider,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import BugReportIcon from '@mui/icons-material/BugReport';
import MovieIcon from '@mui/icons-material/Movie';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useMe, useProjects, useCreateProject } from '../api/hooks.js';
import { api } from '../api/client.js';
import { MetricCard } from '../components/MetricCard.js';
import { useT } from '../i18n/useT.js';
import type { Copy } from '../i18n/copy/index.js';

interface TicketCounts { all: number; stats: { open: number; byStatus: Record<string, number> } }
interface Frustration { summary: { totalSessions: number; avgFrustration: number; highFrustrationSessions: number }; sessions: { sessionId: string; projectId: string; projectName: string; user: string; frustrationScore: number; rageClicks: number; errors: number }[] }
interface Errors { summary: { totalGroups: number; openGroups: number; totalOccurrences: number }; groups: { id: string; kind: string; message: string; count: number }[] }
interface AlertEvt {
  id: string;
  ruleName: string | null;
  planLimit: { metric: string; level: number; limit: number } | null;
  message: string;
  createdAt: string;
}

const KIND_COLOR: Record<string, string> = { JS: '#ef4444', CONSOLE: '#f59e0b', NETWORK: '#3b82f6' };

export function DashboardPage() {
  const all = useT();
  const t = all.dashboard;
  const navigate = useNavigate();
  const { data: me } = useMe();
  const org = me?.organizations[0];
  const orgId = org?.id;
  const base = `/v1/orgs/${orgId}`;

  const { data: projects } = useProjects(orgId);
  const createProject = useCreateProject(orgId);
  const [newOpen, setNewOpen] = useState(false);
  const [projectName, setProjectName] = useState('');
  const createFirstProject = async () => {
    if (!projectName.trim()) return;
    await createProject.mutateAsync({ name: projectName.trim() });
    setNewOpen(false);
    setProjectName('');
  };

  const tickets = useQuery({ enabled: !!orgId, queryKey: ['dash-tickets', orgId], queryFn: async () => (await api.get(`${base}/tickets/counts`)).data as TicketCounts });
  const frustration = useQuery({ enabled: !!orgId, queryKey: ['dash-frustration', orgId], queryFn: async () => (await api.get(`${base}/frustration?days=30&limit=6`)).data as Frustration });
  const errors = useQuery({ enabled: !!orgId, queryKey: ['dash-errors', orgId], queryFn: async () => (await api.get(`${base}/errors?limit=6`)).data as Errors });
  const alerts = useQuery({ enabled: !!orgId, queryKey: ['dash-alerts', orgId], queryFn: async () => (await api.get(`${base}/alerts/events`)).data.data as AlertEvt[] });

  const loading = tickets.isLoading || frustration.isLoading || errors.isLoading;
  const fr = frustration.data?.summary;
  const alerts24h = (alerts.data ?? []).filter((e) => Date.now() - new Date(e.createdAt).getTime() < 86_400_000).length;

  const kpis = [
    { label: t.kpi.openTickets, value: tickets.data?.stats.open ?? 0, icon: <ConfirmationNumberIcon fontSize="small" />, to: '/tickets', accent: '#2ec2b3' },
    { label: t.kpi.highFrustration, value: fr?.highFrustrationSessions ?? 0, icon: <LocalFireDepartmentIcon fontSize="small" />, to: '/signals', accent: '#ef4444' },
    { label: t.kpi.openErrors, value: errors.data?.summary.openGroups ?? 0, icon: <BugReportIcon fontSize="small" />, to: '/errors', accent: '#f59e0b' },
    { label: t.kpi.sessions30d, value: fr?.totalSessions ?? 0, icon: <MovieIcon fontSize="small" />, to: '/signals', accent: '#3b82f6' },
    { label: t.kpi.avgFrustration, value: `${fr?.avgFrustration ?? 0}%`, icon: <SentimentDissatisfiedIcon fontSize="small" />, to: '/user-behavior', accent: '#8b5cf6' },
    { label: t.kpi.alerts24h, value: alerts24h, icon: <NotificationsActiveIcon fontSize="small" />, to: '/alerts', accent: '#f43f5e' },
  ];

  // First run: an org with no project yet gets a focused create-project screen
  // instead of an empty dashboard.
  if (projects && projects.length === 0) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '70vh' }}>
        <Paper sx={{ p: { xs: 4, sm: 6 }, maxWidth: 560, textAlign: 'center' }}>
          <Box sx={{ width: 64, height: 64, mx: 'auto', mb: 2, borderRadius: 3, display: 'grid', placeItems: 'center', bgcolor: 'primary.main', color: 'primary.contrastText' }}>
            <RocketLaunchIcon sx={{ fontSize: 32 }} />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 800 }}>
            {t.firstRun.welcome}{org?.name ? `, ${org.name}` : ''}
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1, mb: 3 }}>
            {t.firstRun.body}
          </Typography>
          <Button size="large" variant="contained" startIcon={<AddIcon />} onClick={() => setNewOpen(true)}>
            {t.firstRun.cta}
          </Button>
        </Paper>

        <Dialog open={newOpen} onClose={() => setNewOpen(false)} fullWidth maxWidth="xs">
          <DialogTitle>{t.firstRun.dialogTitle}</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              fullWidth
              margin="dense"
              label={t.firstRun.nameLabel}
              placeholder={t.firstRun.namePlaceholder}
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && createFirstProject()}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setNewOpen(false)}>{all.common.cancel}</Button>
            <Button variant="contained" onClick={createFirstProject} disabled={!projectName.trim() || createProject.isPending}>
              {all.common.create}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5">{t.title}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {org?.name ? `${org.name} · ` : ''}{t.subtitle}
      </Typography>
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Box sx={{ display: 'grid', gap: 1.5, mb: 2, gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(6, 1fr)' } }}>
        {kpis.map((k) => (
          <MetricCard
            key={k.label}
            icon={k.icon}
            label={k.label}
            value={String(k.value)}
            accent={k.accent}
            onClick={() => navigate(k.to)}
          />
        ))}
      </Box>

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '3fr 2fr' }, mb: 2 }}>
        {/* Most frustrated sessions */}
        <Panel title={t.panels.frustrated} viewAll={t.panels.viewAll} onAll={() => navigate('/signals')}>
          <Stack divider={<Divider flexItem />}>
            {(frustration.data?.sessions ?? []).map((s) => (
              <Stack key={s.sessionId} direction="row" alignItems="center" spacing={1.5} sx={{ py: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                onClick={() => navigate(`/projects/${s.projectId}/sessions/${s.sessionId}`)}>
                <Box sx={{ width: 44, textAlign: 'center' }}>
                  <Typography variant="body2" fontWeight={700} sx={{ color: s.frustrationScore >= 70 ? '#ef4444' : s.frustrationScore >= 40 ? '#f59e0b' : '#64748b' }}>
                    {s.frustrationScore}%
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" noWrap>{s.user}</Typography>
                  <Typography variant="caption" color="text.secondary">{s.projectName}</Typography>
                </Box>
                <Stack direction="row" spacing={0.5}>
                  {!!s.rageClicks && <Chip size="small" label={`${t.chips.rage} ${s.rageClicks}`} sx={{ bgcolor: '#ef444422', color: '#ef4444' }} />}
                  {!!s.errors && <Chip size="small" label={`${t.chips.err} ${s.errors}`} variant="outlined" />}
                </Stack>
                <PlayArrowIcon fontSize="small" sx={{ color: 'text.disabled' }} />
              </Stack>
            ))}
            {frustration.data && frustration.data.sessions.length === 0 && <Empty text={t.empty.frustrated} />}
          </Stack>
        </Panel>

        {/* Recent alerts */}
        <Panel title={t.panels.recentAlerts} viewAll={t.panels.viewAll} onAll={() => navigate('/alerts')}>
          <Stack divider={<Divider flexItem />}>
            {(alerts.data ?? []).slice(0, 6).map((a) => (
              <Box key={a.id} sx={{ py: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                  <Typography variant="body2" fontWeight={600}>{a.planLimit ? all.shell.limits.title : a.ruleName}</Typography>
                  <Typography variant="caption" color="text.disabled">{timeAgo(a.createdAt, t.ago)}</Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {a.planLimit ? all.shell.limits.notice(a.planLimit.metric, a.planLimit.level) : a.message}
                </Typography>
              </Box>
            ))}
            {alerts.data && alerts.data.length === 0 && <Empty text={t.empty.alerts} />}
          </Stack>
        </Panel>
      </Box>

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '3fr 2fr' } }}>
        {/* Top errors */}
        <Panel title={t.panels.topErrors} viewAll={t.panels.viewAll} onAll={() => navigate('/errors')}>
          <Stack divider={<Divider flexItem />}>
            {(errors.data?.groups ?? []).map((g) => (
              <Stack key={g.id} direction="row" alignItems="center" spacing={1.5} sx={{ py: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                onClick={() => navigate(`/errors/${g.id}`)}>
                <Chip size="small" label={g.kind} sx={{ bgcolor: `${KIND_COLOR[g.kind] ?? '#64748b'}22`, color: KIND_COLOR[g.kind] ?? '#64748b', fontWeight: 700, minWidth: 74 }} />
                <Typography variant="body2" noWrap sx={{ flex: 1, fontFamily: 'ui-monospace, monospace', fontSize: 13 }}>{g.message}</Typography>
                <Typography variant="body2" fontWeight={700}>{g.count}</Typography>
              </Stack>
            ))}
            {errors.data && errors.data.groups.length === 0 && <Empty text={t.empty.errors} />}
          </Stack>
        </Panel>

        {/* Tickets snapshot */}
        <Panel title={t.panels.ticketsByStatus} viewAll={t.panels.viewAll} onAll={() => navigate('/tickets')}>
          <Stack spacing={1} sx={{ mb: 1.5 }}>
            {Object.entries(tickets.data?.stats.byStatus ?? {}).map(([status, n]) => (
              <Stack key={status} direction="row" justifyContent="space-between">
                <Typography variant="body2">{status.charAt(0) + status.slice(1).toLowerCase()}</Typography>
                <Typography variant="body2" fontWeight={700}>{n}</Typography>
              </Stack>
            ))}
            {tickets.data && Object.keys(tickets.data.stats.byStatus).length === 0 && <Empty text={t.empty.tickets} />}
          </Stack>
          <Button fullWidth variant="outlined" size="small" startIcon={<ConfirmationNumberIcon />} onClick={() => navigate('/tickets')}>
            {t.panels.openTickets}
          </Button>
        </Panel>
      </Box>
    </Box>
  );
}

function Panel({
  title,
  viewAll,
  onAll,
  children,
}: {
  title: string;
  viewAll: string;
  onAll?: () => void;
  children: React.ReactNode;
}) {
  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography variant="subtitle1" fontWeight={700}>{title}</Typography>
        {onAll && <Button size="small" onClick={onAll}>{viewAll}</Button>}
      </Stack>
      <Divider sx={{ mb: 0.5 }} />
      {children}
    </Paper>
  );
}

function Empty({ text }: { text: string }) {
  return <Typography color="text.secondary" variant="body2" sx={{ py: 2, textAlign: 'center' }}>{text}</Typography>;
}

function timeAgo(iso: string, ago: Copy['dashboard']['ago']): string {
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (m < 1) return ago.justNow;
  if (m < 60) return ago.minutes(m);
  const h = Math.round(m / 60);
  if (h < 24) return ago.hours(h);
  return ago.days(Math.round(h / 24));
}
